import { boardState, BoardStatus, setStatus } from 'common/modules/app/board';
import { GlossaryState } from 'common/modules/app/glossaries';
import {
  DeviceActivateInitialState,
  PaidFeaturesInfo,
  postDevicesActivationReserveCancel,
  resetDevicesActivation
} from 'common/modules/domain/deviceActivate';
import { loadCookie, SasCookieKeys } from 'common/utils/cookie-utils';
import { Glossary } from 'common/utils/glossary';
import { AppState } from 'pages';
import { useDispatch, useSelector } from 'react-redux';
import { StateType } from 'common/modules/domain/deviceActivate';
import ReqFileDeleteConfirmationBoard from '../components/ReqFileDeleteConfirmationBoard';
import { UserInformation } from 'common/modules/domain/user';

/**
 * StoreのStateからComponentに渡すPropsへ
 */
const useStateProps = () => {
  const boardState = useSelector<AppState, boardState>(
    (state) => state.app.board
  );
  const glState = useSelector<AppState, GlossaryState>(
    (state) => state.app.glossaries
  );
  const deviceActivateState = useSelector<AppState, DeviceActivateInitialState>(
    (state) => state.domain.deviceActivate
  );
  const userState = useSelector<AppState, UserInformation>(
    (state) => state.domain.user
  );

  return {
    user: userState,
    isDisplay:
      boardState.status ===
      BoardStatus.DEVICE_ACTIVATE_DELETE_CONFIRMATION_DISP,
    purchaseIdForDeviceActivation: boardState.selectedPurchaseIdForTerminal,
    glossary: Glossary.instance(glState),
    deviceActivate: deviceActivateState
  };
};

type SelectedState = ReturnType<typeof useStateProps>;

/**
 * Actionをdispatchする関数からComponentに渡すPropsへ
 */
const useMargeProps = (state: SelectedState) => {
  const dispatch = useDispatch();
  const cookie = loadCookie();
  const _token = cookie[SasCookieKeys.KEY_TOKEN];
  const _refreshToken = cookie[SasCookieKeys.KEY_REFRESH_TOKEN];
  const _purchaseIdForDeviceActivation = state.purchaseIdForDeviceActivation;
  const _language = state.user.language;
  const _deviceActivate = state.deviceActivate;
  /** 選択中のユーザー注文番号に紐づく有償機能情報を取得 */
  const paidFeaturesInfo: PaidFeaturesInfo =
    _deviceActivate.deviceActivateMap[_purchaseIdForDeviceActivation];

  /** 有償機能アクティベート情報リストマップを作成 */
  const _deviceActivateMap: {
    [key: string]: {
      /** モデル名 */
      modelName: string;
      /** 状態("active"：有効 or "reserve"：予約) */
      status: StateType;
      /** 機種ID */
      modelId: string;
      /** シリアル番号 */
      serialNo: string;
      /** アップロード日時 (ISO8601 UTC) */
      uploadAt: string;
      /** 削除チェックボックスの状態(true or false) */
      isDelete: boolean;
    };
  } = {};
  if (paidFeaturesInfo != null) {
    if (paidFeaturesInfo.activates != null) {
      for (const terminalId of Object.keys(paidFeaturesInfo.activates)) {
        const activate = paidFeaturesInfo.activates[terminalId];
        _deviceActivateMap[terminalId] = {
          modelName: activate.deviceActivateInfo.modelName,
          status: activate.deviceActivateInfo.status,
          modelId: activate.deviceActivateInfo.modelId,
          serialNo: activate.deviceActivateInfo.serialNo,
          uploadAt: activate.deviceActivateInfo.uploadAt,
          isDelete: activate.isDelete
        };
      }
    }
  }

  // 閉じるボタンの処理
  const onClickClose = () => {
    // 機能アクティベート管理画面を表示
    dispatch(setStatus(BoardStatus.DEVICE_ACTIVATE_DISP));
  };

  // はい(削除)ボタンの処理
  const onClickYes = () => {
    // 有償機能アクティベート情報一覧をクリア
    dispatch(resetDevicesActivation());
    // リクエストファイル削除時の確認ダイアログを閉じる
    dispatch(setStatus(BoardStatus.DEVICE_ACTIVATE_DISP));

    const datas = [];
    for (const value of Object.values(_deviceActivateMap)) {
      if (value.isDelete) {
        datas.push({
          modelId: value.modelId,
          serialNo: value.serialNo
        });
      }
    }
    // 削除チェックボックスの状態がtrueの場合取り消しAPIを実行する
    dispatch(
      postDevicesActivationReserveCancel(
        _token,
        _refreshToken,
        _purchaseIdForDeviceActivation,
        datas,
        state.glossary,
        _language,
        false
      )
    );
  };

  return {
    onClickClose: onClickClose,
    onClickYes: onClickYes
  };
};

/**
 * リクエストファイル削除時の確認ダイアログ全体のContainer Component
 */
export default function ContainerComponent(ownProps: { isMobile?: boolean }) {
  const _props = { ...useStateProps(), ...useMargeProps(useStateProps()) };
  return <ReqFileDeleteConfirmationBoard {...{ ..._props, ...ownProps }} />;
}
